/* Bundle builder (Price Line).
   State: a list of "units" (one per shirt), each a product id. Every unit
   takes the size from the size row. Counts, tier, prices and totals are
   derived from that list and the ladder in section settings. Adds every line to the cart in one request,
   then opens the theme's cart drawer. */
(function () {
  'use strict';
  if (window.customElements.get('bundle-builder')) return;

  const fmtCache = {};
  function money(amount, currency) {
    const key = currency || 'USD';
    if (!fmtCache[key]) {
      try {
        fmtCache[key] = new Intl.NumberFormat('en-US', { style: 'currency', currency: key });
      } catch (e) {
        fmtCache[key] = { format: (n) => '$' + Number(n).toFixed(2) };
      }
    }
    return fmtCache[key].format(amount);
  }
  function fill(tpl, vars) {
    return String(tpl || '').replace(/\{(\w+)\}/g, (m, k) => (k in vars ? vars[k] : m));
  }
  function el(tag, cls, html) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      try {
        this.config = JSON.parse(this.querySelector('[data-bb-config]').textContent);
        this.products = JSON.parse(this.querySelector('[data-bb-products]').textContent);
      } catch (e) {
        console.error('[bundle-builder] bad JSON', e);
        return;
      }
      this.currency = this.config.currency || 'USD';
      this.text = this.config.text || {};
      this.tiers = (this.config.tiers || [])
        .map((t) => ({ qty: parseInt(t.qty, 10) || 0, price: parseFloat(String(t.price).replace(/[^0-9.]/g, '')) || 0 }))
        .filter((t) => t.qty > 0 && t.price > 0)
        .sort((a, b) => a.qty - b.qty);
      if (!this.tiers.length) this.tiers = [{ qty: 1, price: 49.99 }];
      this.basePrice = this.tiers[0].price;
      this.sizes = (this.config.sizes || []).map((s) => String(s).trim()).filter(Boolean);
      this.size = this.sizes.includes(this.config.defaultSize) ? this.config.defaultSize : this.sizes[0];
      this.units = []; // {pid}
      this.byId = {};
      this.products.forEach((p) => {
        p.vBySize = {};
        p.variants.forEach((v) => { p.vBySize[v.size] = v; });
        this.byId[p.id] = p;
      });

      this.$ = (sel) => this.querySelector(sel);
      this.$price = this.$('[data-bb-price]');
      this.$compare = this.$('[data-bb-compare]');
      this.$count = this.$('[data-bb-count]');
      this.$segs = this.$('[data-bb-segs]');
      this.$seglabels = this.$('[data-bb-seglabels]');
      this.$nudge = this.$('[data-bb-nudge]');
      this.$whys = Array.from(this.querySelectorAll('[data-bb-why]')); // one under the title (phones), one under the colors (desktop)
      this.$sizes = this.$('[data-bb-sizes]');
      this.$picked = this.$('[data-bb-picked]');
      this.$grid = this.$('[data-bb-grid]');
      this.$total = this.$('[data-bb-total]');
      this.$totline = this.$('[data-bb-totline]');
      this.$add = this.$('[data-bb-add]');
      this.$toast = this.$('[data-bb-toast]');

      this.buildSizes();
      this.buildGrid();
      this.buildBar();
      this.render();

      this.$add.addEventListener('click', () => this.addToCart());
    }

    /* ---------- derived ---------- */
    variantFor(pid, size) { const p = this.byId[pid]; return p ? p.vBySize[size] : null; }
    isAvailable(pid, size) { const v = this.variantFor(pid, size); return !!(v && v.available); }
    countFor(pid) { return this.units.filter((u) => u.pid === pid).length; }
    countForVariant(pid, size) { return size === this.size ? this.countFor(pid) : 0; }
    total() { return this.units.length; }
    tierFor(n) {
      let t = null;
      for (const tier of this.tiers) if (n >= tier.qty) t = tier;
      return t;
    }
    nextTierFor(n) { return this.tiers.find((t) => t.qty > n) || null; }
    /* Can one more of this variant be added within tracked stock? */
    stockRoom(pid, size) {
      const v = this.variantFor(pid, size);
      if (!v || !v.available) return 0;
      if (!v.managed || v.policy === 'continue') return Infinity;
      return Math.max(0, v.qty - this.countForVariant(pid, size));
    }

    /* ---------- build ---------- */
    buildSizes() {
      this.$sizes.innerHTML = '';
      this.sizes.forEach((s) => {
        const b = el('button', 'bb__size', s);
        b.type = 'button';
        b.dataset.size = s;
        b.setAttribute('role', 'radio');
        b.addEventListener('click', () => this.setSize(s));
        this.$sizes.appendChild(b);
      });
    }
    buildGrid() {
      this.$grid.innerHTML = '';
      this.products.forEach((p) => {
        const t = el('div', 'bb__tile');
        t.dataset.pid = p.id;
        t.setAttribute('role', 'button');
        t.setAttribute('tabindex', '0');
        t.setAttribute('aria-label', p.color);
        const img = el('img', 'bb__thumb');
        img.src = p.image || '';
        img.alt = '';
        img.loading = 'lazy';
        img.width = 120; img.height = 120;
        const minus = el('button', 'bb__minus', '&minus;');
        minus.type = 'button';
        minus.setAttribute('aria-label', 'Remove one ' + p.color);
        const badge = el('span', 'bb__badge', '0');
        const name = el('span', 'bb__tname', p.color);
        const soldout = el('span', 'bb__soldout', '');
        t.append(img, minus, badge, soldout, name);
        t.addEventListener('click', (e) => {
          if (e.target === minus || e.target === badge) { this.removeOne(p.id); return; }
          this.addOne(p.id);
        });
        t.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.addOne(p.id); }
          if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); this.removeOne(p.id); }
        });
        this.$grid.appendChild(t);
      });
    }
    buildBar() {
      this.$segs.innerHTML = '';
      this.$seglabels.innerHTML = '';
      this.tiers.forEach(() => {
        this.$segs.appendChild(el('i'));
        this.$seglabels.appendChild(el('span'));
      });
    }

    /* ---------- actions ---------- */
    setSize(size) {
      if (!this.sizes.includes(size) || size === this.size) return;
      // Shirts whose color has no stock in the new size come off the count.
      this.units = this.units.filter((u) => this.isAvailable(u.pid, size));
      this.size = size;
      this.render();
    }
    addOne(pid) {
      const p = this.byId[pid];
      if (!p) return;
      const size = this.size;
      const v = this.variantFor(pid, size);
      if (!v || !v.available) return;
      const room = this.stockRoom(pid, size);
      if (room <= 0) { this.toast(fill(this.text.onlyLeft, { count: v.qty, size })); return; }
      this.units.push({ pid });
      this.render();
    }
    removeOne(pid) {
      for (let i = this.units.length - 1; i >= 0; i--) {
        if (this.units[i].pid === pid) { this.units.splice(i, 1); break; }
      }
      this.render();
    }

    /* ---------- render ---------- */
    render() {
      const n = this.total();
      const tier = this.tierFor(n) || this.tiers[0];
      const next = this.nextTierFor(n);
      const price = tier.price;
      const cur = this.currency;

      // price line
      this.$price.textContent = money(price, cur);
      if (n > 0 && price < this.basePrice) {
        this.$compare.textContent = money(this.basePrice, cur);
        this.$compare.hidden = false;
      } else {
        this.$compare.hidden = true;
      }
      this.$count.textContent = n + ' ' + (n === 1 ? this.text.shirtOne : this.text.shirtMany);

      // bar
      const segs = this.$segs.children, labels = this.$seglabels.children;
      this.tiers.forEach((t, i) => {
        const on = n >= t.qty;
        const isNext = next && t.qty === next.qty;
        segs[i].className = on ? 'on' : (isNext ? 'next' : '');
        labels[i].className = on ? 'on' : (isNext ? 'next' : '');
        labels[i].textContent = on ? String(t.qty) : t.qty + ' · ' + money(t.price, cur);
      });

      // live line under the title
      this.$nudge.classList.toggle('is-live', n > 0);
      if (n === 0) {
        this.$nudge.innerHTML = this.text.nudgeEmpty || ''; // setting may hold <br> for a line break
        this.setWhy(this.text.whyEmpty || '');
      } else if (next) {
        // What the next tier costs compared with the cart right now. When the
        // whole cart gets cheaper, or the extra shirts cost less than half the
        // next price each, say that instead of the per-shirt price.
        const more = next.qty - n;
        const delta = Math.round((next.qty * next.price - n * price) * 100) / 100;
        const vars = {
          count: '<b>' + more + '</b>',
          price: '<b>' + money(next.price, cur) + '</b>',
          delta: '<b>' + money(Math.abs(delta), cur) + '</b>'
        };
        let tpl = this.text.nudgeNext;
        if (delta <= 0 && this.text.nudgeNextLess) tpl = this.text.nudgeNextLess;
        else if (delta / more < next.price / 2 && this.text.nudgeNextCheap) tpl = this.text.nudgeNextCheap;
        this.$nudge.innerHTML = fill(tpl, vars);
        // Highlighted second line: only when the maths is surprising. "Cheaper"
        // when the next tier's total is below today's; "barely moves" when the
        // extra shirts cost under a quarter of the next price each.
        const pct = Math.round((1 - next.price / this.basePrice) * 100) + '%';
        const whyVars = { count: more, shirts: more === 1 ? this.text.shirtOne : this.text.shirtMany, pct, next: next.qty, now: n, delta: money(Math.abs(delta), cur) };
        let why = '';
        if (delta <= 0 && this.text.whyLess) why = this.text.whyLess;
        else if (delta / more < next.price / 4 && this.text.whyCheap) why = this.text.whyCheap;
        this.setWhy(why ? fill(why, whyVars) : '');
      } else {
        this.$nudge.innerHTML = fill(this.text.nudgeMax, { count: n, price: '<b>' + money(price, cur) + '</b>' });
        this.setWhy('');
      }

      // sizes row: a size is off when no color has it in stock
      Array.from(this.$sizes.children).forEach((b) => {
        const s = b.dataset.size;
        const any = this.products.some((p) => this.isAvailable(p.id, s));
        b.classList.toggle('is-on', s === this.size);
        b.classList.toggle('is-off', !any);
        b.setAttribute('aria-checked', s === this.size ? 'true' : 'false');
        b.disabled = !any;
      });

      // tiles
      let picked = 0;
      Array.from(this.$grid.children).forEach((t) => {
        const pid = Number(t.dataset.pid);
        const c = this.countFor(pid);
        if (c > 0) picked++;
        const off = !this.isAvailable(pid, this.size);
        t.classList.toggle('is-sel', c > 0);
        t.classList.toggle('is-off', off && c === 0);
        t.querySelector('.bb__badge').textContent = c;
        t.querySelector('.bb__soldout').textContent = off ? this.text.soldOut : '';
        t.setAttribute('aria-disabled', off && c === 0 ? 'true' : 'false');
      });
      this.$picked.textContent = picked + ' ' + this.text.picked;

      // bottom bar
      const total = n * price;
      this.$total.textContent = money(total, cur);
      if (n === 0) {
        this.$totline.textContent = '';
        this.$add.textContent = this.text.buttonEmpty;
        this.$add.disabled = true;
      } else {
        this.$totline.innerHTML = n + ' × ' + money(price, cur) + (price < this.basePrice ? ' <s>' + money(this.basePrice, cur) + '</s>' : '');
        this.$add.textContent = fill(this.text.button, { count: n });
        this.$add.disabled = false;
      }
      this.dispatchEvent(new CustomEvent('bundle-builder:change', { bubbles: true, detail: { count: n, price, total, tier } }));
    }

    setWhy(text) {
      this.$whys.forEach((el) => {
        if (!text) { el.hidden = true; el.innerHTML = ''; return; }
        el.innerHTML = '<mark>' + text + '</mark>';
        el.hidden = false;
      });
    }

    /* ---------- cart ---------- */
    lines() {
      const map = {};
      this.units.forEach((u) => {
        const v = this.variantFor(u.pid, this.size);
        if (!v) return;
        map[v.id] = (map[v.id] || 0) + 1;
      });
      return Object.keys(map).map((id) => ({ id: Number(id), quantity: map[id] }));
    }
    async addToCart() {
      const items = this.lines();
      if (!items.length || this.busy) return;
      this.busy = true;
      const label = this.$add.textContent;
      this.$add.textContent = this.text.buttonAdding || label;
      this.$add.classList.add('is-busy');
      try {
        const url = (this.config.routes && this.config.routes.cartAdd) || '/cart/add';
        const res = await fetch(url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          this.toast(data.description || data.message || this.text.cartError);
          return;
        }
        this.units = [];
        this.render();
        await this.applyCodes();
        const drawer = document.querySelector('m-cart-drawer');
        if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
          drawer.onCartDrawerUpdate();
          drawer.open();
        } else {
          window.location.href = (this.config.routes && this.config.routes.cart) || '/cart';
        }
      } catch (e) {
        console.error('[bundle-builder] add failed', e);
        this.toast(this.text.cartError);
      } finally {
        this.busy = false;
        this.$add.classList.remove('is-busy');
        this.render();
      }
    }

    /* Put the ladder's discount codes on the cart so Shopify charges the tier
       price. Existing codes on the cart are kept (Shopify allows 5 per order);
       the update replaces the whole list, so merge first. Never blocks the add. */
    async applyCodes() {
      const ours = String(this.config.codes || '').split(',').map((c) => c.trim().toUpperCase()).filter(Boolean);
      if (!ours.length) return;
      try {
        const root = (this.config.routes && this.config.routes.cart) || '/cart';
        const cart = await fetch(root + '.js', { headers: { 'Accept': 'application/json' } }).then((r) => r.json());
        const existing = (cart.discount_codes || []).map((d) => String(d.code).toUpperCase());
        const merged = existing.slice();
        ours.forEach((c) => { if (!merged.includes(c)) merged.push(c); });
        // Remember the codes for ladder-codes.js, which re-applies them after
        // checkout strips the ones the cart does not qualify for yet.
        document.cookie = 'neat_ladder=' + encodeURIComponent(ours.join(',')) + '; path=/; max-age=2592000; SameSite=Lax';
        if (merged.length === existing.length) return;
        const url = (this.config.routes && this.config.routes.cartUpdate) || '/cart/update';
        await fetch(url + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: merged.join(',') })
        });
      } catch (e) {
        console.warn('[bundle-builder] could not apply discount codes', e);
      }
    }

    toast(msg) {
      if (!msg) return;
      this.$toast.textContent = msg;
      this.$toast.hidden = false;
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => { this.$toast.hidden = true; }, 2600);
    }
  }

  customElements.define('bundle-builder', BundleBuilder);
})();
